import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import datalabelsPlugin from 'chartjs-plugin-datalabels';
import { formatDate } from '~/helper/formatDate';
import { formatPrice } from '~/helper/formatPrice';
import { formatPercent } from '~/helper/formatPercent';
import { useChartTheme } from '~/helper/chartTheme';

Chart.register(annotationPlugin, datalabelsPlugin);

type ValueFormat = 'date' | 'currency' | 'string' | 'percent';

type Props = {
  readonly datasetIdKey: string;
  readonly annotations?: Record<
    string,
    {
      yMin: number;
      yMax: number;
      borderColor: string;
    }
  >;
  readonly series?: {
    readonly x: string;
    readonly y: number;
  }[];
  readonly xLabel?: string;
  readonly yLabel?: string;
  readonly xFormat?: ValueFormat;
  readonly yFormat?: ValueFormat;
  readonly displayDatalabels?: boolean;
  readonly colors?: string[];
};

export function BarChart(props: Props) {
  const theme = useChartTheme();
  const formatX = getFormatter(props.xFormat || 'string');
  const formatY = getFormatter(props.yFormat || 'string');

  // Default sind die Serien-Tokens 2 und 3 (accent-300 / secondary-300).
  const colors = props.colors || theme.series.slice(1);

  // Ensure data is always an array
  const dataArray = props.series || [];
  const labels = dataArray.map((item) => formatX(item.x as any));
  const dataPoints = dataArray.map((item) => item.y);

  const chartData: ChartData = {
    labels: labels,
    datasets: [
      {
        data: dataPoints,
        backgroundColor: colors.map((color) => color),
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 20,
      },
    ],
  };

  const options: ChartOptions = {
    indexAxis: props.horizontal ? 'y' : 'x',
    plugins: {
      datalabels: {
        display: props.displayDatalabels ?? false,
        color: theme.datalabel,
        textStrokeWidth: 1,
        anchor: 'center',
        align: 'center',
        formatter: (value) => {
          return formatY(value);
        },
      },
      tooltip: {
        backgroundColor: theme.tooltip.backgroundColor,
        titleColor: theme.tooltip.titleColor,
        bodyColor: theme.tooltip.bodyColor,
        borderColor: theme.tooltip.borderColor,
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          label: (context: any) =>
            getFormatter(props.yFormat || 'string')(
              parseFloat(context.raw.toString() || '0'),
            ),
        },
      },
      legend: {
        display: false,
      },
      annotation: {
        annotations: props.annotations,
      },
    },
    scales: {
      x: {
        type: props.horizontal ? 'linear' : 'category',
        position: props.horizontal ? 'top' : undefined,
        grid: { display: false, borderColor: theme.grid },
        ticks: { color: theme.tick },
        title: {
          display: !!props.yLabel,
          text: props.yLabel || 'Y-Achse',
          color: theme.axisTitle,
        },
      },
      y: {
        type: props.horizontal ? 'category' : 'linear',
        beginAtZero: true,
        grid: {
          color: theme.grid,
          borderColor: theme.grid,
        },
        title: {
          display: !!props.xLabel,
          text: props.xLabel || 'X-Achse',
          color: theme.axisTitle,
        },
        ticks: {
          color: theme.tick,
          // Use integer steps for counts; let percent charts decide automatically
          stepSize: props.yFormat === 'percent' ? undefined : 1,
          callback: (value) => formatY(Number(value)),
        },
      },
    },
  };

  return (
    <Bar
      datasetIdKey={props.datasetIdKey}
      data={chartData as any}
      options={options as any}
    />
  );
}

function getFormatter(format: ValueFormat) {
  switch (format) {
    case 'date':
      return formatDate;
    case 'currency':
      return formatPrice;
    case 'percent':
      return formatPercent;
    default:
      return (value: string | number) => value.toString();
  }
}
